import { useTranslation } from 'react-i18next';
import { PanelHead } from './panel';
import { metricText, type Fmt } from './format';
import type { Report } from './use-report';

/** "Čerpání a výhled": the ring of spend against the approved plan for the period, with the projected pace. */
export function BudgetCard({ budget, fmt }: { budget: NonNullable<Report['budget']>; fmt: Fmt }) {
  const { t } = useTranslation(['performance', 'common']);
  const share = budget.share.value;
  const percent = share == null ? 0 : Math.min(100, share * 100);
  const forecastOver =
    budget.forecastCzk.value != null && budget.planCzk.value != null && budget.planCzk.value > 0
      ? budget.forecastCzk.value / budget.planCzk.value - 1
      : null;
  const tone =
    budget.planCzk.value == null
      ? 'neutral'
      : forecastOver == null
        ? 'neutral'
        : forecastOver > 0.1
          ? 'bad'
          : forecastOver > 0
            ? 'warning'
            : 'good';
  return (
    <article className="pa-panel pa-budget">
      <PanelHead title={t('dashboard.budget.title')} subtitle={t('dashboard.budget.subtitle')}>
        <span className={`pa-tag hp-tag ${tone}`}>{t(`dashboard.budget.tone.${tone}`)}</span>
      </PanelHead>
      <div className="pa-budget-body">
        <div className="pa-budget-ring" style={{ ['--progress' as string]: `${percent}%` }}>
          <div>
            <strong>
              {share == null ? '—' : fmt.dec(share * 100, 0)}
              <small>%</small>
            </strong>
            <span>{t('dashboard.budget.ofPlan')}</span>
          </div>
        </div>
        <dl className="pa-budget-values">
          <div>
            <dt>{t('dashboard.budget.plan')}</dt>
            <dd
              title={
                budget.planCzk.reason
                  ? t(`dashboard.budget.reason.${budget.planCzk.reason}`, { defaultValue: budget.planCzk.reason })
                  : undefined
              }
            >
              {metricText(budget.planCzk, (v) => fmt.compactCzk(v))}
            </dd>
          </div>
          <div>
            <dt>{t('dashboard.budget.spent')}</dt>
            <dd>{fmt.compactCzk(budget.spentCzk)}</dd>
          </div>
          <div>
            <dt>{t('dashboard.budget.remaining')}</dt>
            <dd>{metricText(budget.remainingCzk, (v) => fmt.compactCzk(v))}</dd>
          </div>
          <div>
            <dt>{t('dashboard.budget.forecast')}</dt>
            <dd>{metricText(budget.forecastCzk, (v) => fmt.compactCzk(v))}</dd>
          </div>
        </dl>
      </div>
      <p className="pa-footnote">
        {budget.planCzk.value == null
          ? t('dashboard.budget.noPlan')
          : t('dashboard.budget.footnote', {
              planned: budget.monthsPlanned,
              months: budget.monthsInPeriod,
              elapsed: fmt.dec(budget.elapsedShare * 100, 0),
            })}
      </p>
    </article>
  );
}
